/**
 * Reactions UI for chat messages — Telegram-style.
 *
 *  - <ReactionPills /> renders the existing aggregated reactions as small
 *    clickable pills below a message bubble. Clicking a pill calls onToggle
 *    with the emoji; the parent decides whether that means add/remove/switch
 *    (it doesn't need to — the server resolves it).
 *
 *  - <ReactionPicker /> renders the "+ smile" affordance that pops a small
 *    palette of quick-pick emojis. Visible on hover (parent provides the
 *    `group` class) so it doesn't visually clutter every message; on a touch
 *    screen, which has no hover, it is always visible.
 *
 *  - computeOptimisticReactions(prev, emoji, currentEmoji) returns the new
 *    reactions array as if the toggle had already been applied. Keeps the
 *    optimistic-update logic out of the call site and makes it unit-testable.
 *
 * The quick-pick set MUST stay in sync with `MessageReactions.ALLOWED_EMOJIS`
 * on the backend; the server rejects anything outside that whitelist with 400.
 */
import React, { useState } from "react";
import { Popover } from "@shopify/polaris";
import { Smile } from "lucide-react";
import { MessageReaction } from "types/Chat";
import { FINGER_PADDING, SHOW_ON_HOVER_OR_TOUCH, hoverHandlers } from "./chat-touch";

export const EMOJI_MAP: Record<string, string> = {
  heart: "❤️",
  thumbsup: "👍",
  laugh: "😂",
  fire: "🔥",
  wow: "😮",
  sad: "😢",
  pray: "🙏",
};

export const QUICK_EMOJI_IDS = Object.keys(EMOJI_MAP);

export const ReactionPills: React.FC<{
  reactions: MessageReaction[];
  onToggle: (emojiId: string) => void;
}> = ({ reactions, onToggle }) => {
  if (!reactions || !reactions.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {reactions.map((r) => {
        const symbol = EMOJI_MAP[r.emoji] || r.emoji;
        return (
          <button
            key={r.emoji}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(r.emoji);
            }}
            className={
              "px-1.5 py-0.5 rounded-full text-xs flex items-center gap-1 border transition-colors leading-none " +
              (r.me
                ? "bg-blue-600/30 border-blue-500 text-blue-100 hover:bg-blue-600/40"
                : "bg-gray-700/60 border-gray-600 text-gray-200 hover:bg-gray-700")
            }
            aria-label={`${symbol} ${r.count}`}
            title={r.me ? "Click to remove your reaction" : "Click to add your reaction"}
          >
            <span>{symbol}</span>
            {r.count > 1 && <span className="text-[10px]">{r.count}</span>}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Controlled-only picker. Open/close state is owned by the parent so that
 * only ONE picker can be open at a time across the whole thread (matches
 * the editing-state pattern in ChatThreadView). The parent typically tracks
 * a single `openPickerMessageId` and passes `open={openPickerMessageId === m.id}`.
 */
export const ReactionPicker: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (emoji: string) => void;
}> = ({ open, onOpenChange, onPick }) => {
  const leaveTimeoutRef = React.useRef<number | null>(null);

  const handleEnter = () => {
    if (leaveTimeoutRef.current) window.clearTimeout(leaveTimeoutRef.current);
    onOpenChange(true);
  };

  const handleLeave = () => {
    leaveTimeoutRef.current = window.setTimeout(() => {
      onOpenChange(false);
    }, 200);
  };

  const activator = (
    <button
      type="button"
      {...hoverHandlers(handleEnter, handleLeave)}
      onClick={(e) => {
        e.stopPropagation();
        onOpenChange(!open);
      }}
      className={`${SHOW_ON_HOVER_OR_TOUCH} ${FINGER_PADDING} p-0.5 text-gray-400 hover:text-yellow-400`}
      aria-label="Add reaction"
    >
      <Smile size={14} />
    </button>
  );

  return (
    <Popover
      active={open}
      activator={activator}
      onClose={() => onOpenChange(false)}
      preferredPosition="above"
      fluidContent
    >
      <div
        className="flex gap-1 p-2 bg-gray-900 border border-gray-700 rounded-lg shadow-xl"
        {...hoverHandlers(handleEnter, handleLeave)}
      >
        {QUICK_EMOJI_IDS.map((e) => (
          <button
            key={e}
            type="button"
            onClick={(ev) => {
              ev.stopPropagation();
              onPick(e);
              onOpenChange(false);
            }}
            className="text-lg hover:scale-125 transition-transform px-1 leading-none"
            title={e}
          >
            {EMOJI_MAP[e]}
          </button>
        ))}
      </div>
    </Popover>
  );
};

/**
 * Compute the new reactions array as if `emoji` was just toggled by the
 * current user. Encodes the same toggle logic the server does:
 *   - same emoji as current user's existing → remove (decrement / drop)
 *   - different emoji from current user's existing → switch (decrement old, increment new)
 *   - none → add
 *
 * Pure function — no React, no Redux. Easy to unit-test if needed later.
 */
export function computeOptimisticReactions(
  prev: MessageReaction[],
  emoji: string
): MessageReaction[] {
  const next: MessageReaction[] = prev.map((r) => ({ ...r }));
  const mineIdx = next.findIndex((r) => r.me);
  const targetIdx = next.findIndex((r) => r.emoji === emoji);

  // Remove the user from their current reaction (if any).
  if (mineIdx !== -1) {
    next[mineIdx] = {
      ...next[mineIdx],
      count: Math.max(0, next[mineIdx].count - 1),
      me: false,
    };
  }

  const mineWasOnTarget = mineIdx !== -1 && next[mineIdx].emoji === emoji;

  // Add the user to the new reaction unless they were toggling off the same one.
  if (!mineWasOnTarget) {
    if (targetIdx !== -1) {
      next[targetIdx] = {
        ...next[targetIdx],
        count: next[targetIdx].count + 1,
        me: true,
      };
    } else {
      next.push({ emoji, count: 1, me: true });
    }
  }

  // Drop entries with count 0 (the dropped previous reaction).
  return next.filter((r) => r.count > 0);
}
